import { useState, useEffect } from 'react';

export default function useLocalStorage<ValueType>(
  key: string,
  defaultValue: ValueType
) {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);

    if (storedValue !== null) {
      try {
        return JSON.parse(storedValue);
      } catch (error) {
        console.warn(
          `Invalid JSON in localStorage for key "${key}". Removing...`
        );
        localStorage.removeItem(key);
      }
    }

    return defaultValue;
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: any) => {
      const result =
        typeof newValue === 'function' ? newValue(currentValue) : newValue;

      localStorage.setItem(key, JSON.stringify(result));

      return result;
    });
  };

  const removeValueInLocalStorage = () => {
    localStorage.removeItem(key);
    setValue(defaultValue);
  };

  return [value, setValueInLocalStorage, removeValueInLocalStorage];
}
